import { createSlice } from '@reduxjs/toolkit';
import { TopicState, TopicPreviewData, TopicFullData } from 'src/types/state/topic';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { suApiSlice } from './api/su';
import { TopicApiData } from '../../types/api/topic';
import type { AppState } from '../store';

// State of the Topics Slice
export interface TopicsSliceState {
  byId: Record<string, TopicState>
}

export const initialState: TopicsSliceState = {
  byId: {},
};

export const topicsSlice = createSlice({
  name: 'topics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(
        suApiSlice.endpoints.listTopics.matchFulfilled,
        (state, action) => {
          // Process topics data into our store from the ListTopics API
          try {
            const topicsById: Record<string, TopicState> = {};

            // Process each topic in the response
            action.payload.topics.forEach((topicData: TopicApiData) => {
              // Validate required fields
              if (!topicData || !topicData.topicId) {
                ClientLogger.error(ClientLog.IllegalInternalDataState, {
                  payload: topicData,
                  url: window.location.href,
                });
                return;
              }

              // Extract subtopic IDs if available
              const subtopicIds = topicData.subTopicList?.map((subtopic) => subtopic.subtopicId) || [];

              // Create preview data
              const previewData: TopicPreviewData = {
                previewDescription: topicData.description,
                imgSrc: topicData.thumbnailLink,
              };

              // Store the topic data with preview variant
              topicsById[topicData.topicId] = {
                topicId: topicData.topicId,
                title: topicData.title,
                subtopicIds,
                marketplaceId: topicData.marketplaceId,
                locale: topicData.locale,
                preview: previewData,
              };
            });

            // Update the state with the processed topics
            state.byId = {
              ...state.byId,
              ...topicsById,
            };
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.getTopic.matchFulfilled,
        (state, action) => {
          try {
            const topicData = action.payload.topic;

            if (!topicData?.topicId) {
              ClientLogger.error(ClientLog.IllegalInternalDataState, {
                payload: topicData,
                url: window.location.href,
              });
              return;
            }

            const subtopicIds = topicData.subTopicList?.map((subtopic) => subtopic.subtopicId) || [];

            const fullData: TopicFullData = {
              description: topicData.description,
              learningPathwayIds: topicData.learningPathways || [],
              pageHeaderImgSrc: topicData.thumbnailLink,
            };

            // Update existing topic or create new one with full data
            state.byId[topicData.topicId] = {
              ...state.byId[topicData.topicId],
              topicId: topicData.topicId,
              title: topicData.title,
              subtopicIds,
              marketplaceId: topicData.marketplaceId,
              locale: topicData.locale,
              full: fullData,
            };
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              actionType: action.type,
              payload: action.payload,
              url: window.location.href,
            });
          }
        },
      );
  },
});

// Basic selectors
export const topicsActions = topicsSlice.actions;
export const selectTopicById = (
  state: AppState,
  topicId: string,
) => {
  return state.topics.byId[topicId];
};
export const selectAllTopics = (state: AppState) => {
  return Object.values(state.topics.byId);
};

// Preview-specific selectors
export const selectTopicsWithPreview = (state: AppState) => {
  return Object.values(state.topics.byId).filter((topic) => !!topic.preview);
};
